import { real_id } from 'src/helpers/interface_helpers';
import { devices_settings, devices_status } from 'src/stores';
import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import { getBthomeDeviceModel } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import type { VirtualComponentInterface } from 'src/types/VirtualComponents';

const BTHOME_CONTROL_REGEX = /^bthomecontrol:2\d{2}$/;
const BTHOME_DEVICE_REGEX = /^bthomedevice:2\d{2}$/;
const BTHOME_SENSOR_REGEX = /^bthomesensor:2\d{2}$/;

/** BTHome models that act as a remote (same rule as Remote Controls UI). */
const REMOTE_DEVICE_TYPES = new Set(['remote_control', 'button']);

/** BTHome object IDs that represent remote event sources (button/dimmer/rotation/channel). */
const REMOTE_EVENT_OBJ_IDS = new Set([58, 60, 63, 96]);

type ComponentMap = Record<string, any>;

export type BtHomeStatusIconFlags = {
	hasPairedBTHomeDevices: boolean;
	hasPairedBluetoothRemotes: boolean;
};

const EMPTY_FLAGS: BtHomeStatusIconFlags = {
	hasPairedBTHomeDevices: false,
	hasPairedBluetoothRemotes: false,
};

const getComponentMaps = (
	device: Device,
	statusOverride?: ComponentMap,
	settingsOverride?: DeviceSettingsG2 | ComponentMap,
): { statusMap: ComponentMap; settingsMap: ComponentMap } | null => {
	if (!device?.id) {
		return null;
	}

	const deviceID = real_id(device.id);
	return {
		statusMap: (statusOverride || get(devices_status)?.[deviceID] || {}) as ComponentMap,
		settingsMap: (settingsOverride || get(devices_settings)?.[deviceID] || {}) as ComponentMap,
	};
};

const hasComponent = (maps: ComponentMap[], regex: RegExp): boolean =>
	maps.some((map) => Object.keys(map).some((key) => regex.test(key)));

const isRemoteModel = (componentStatus: ComponentMap | undefined): boolean => {
	if (!componentStatus) {
		return false;
	}

	const model = getBthomeDeviceModel(componentStatus as VirtualComponentInterface['status']);
	return !!model && REMOTE_DEVICE_TYPES.has(model.deviceType || '');
};

const hasRemoteEventSensor = (settingsMap: ComponentMap): boolean =>
	Object.entries(settingsMap).some(([key, config]) => {
		if (!BTHOME_SENSOR_REGEX.test(key) || !config || typeof config !== 'object') {
			return false;
		}

		const objId = (config as ComponentMap).obj_id;
		return typeof objId === 'number' && REMOTE_EVENT_OBJ_IDS.has(objId);
	});

/** A paired BTHome device exists when the host owns a bthomedevice component. */
const hasBTHomeDeviceComponent = (statusMap: ComponentMap, settingsMap: ComponentMap): boolean =>
	hasComponent([statusMap, settingsMap], BTHOME_DEVICE_REGEX);

/**
 * A paired remote exists when the host has a remote-control component, a BTHome device of a
 * remote/button model, or sensors that expose remote events.
 */
const hasPairedRemote = (statusMap: ComponentMap, settingsMap: ComponentMap): boolean => {
	if (hasComponent([statusMap, settingsMap], BTHOME_CONTROL_REGEX)) {
		return true;
	}

	if (hasRemoteEventSensor(settingsMap)) {
		return true;
	}

	return [...Object.keys(statusMap), ...Object.keys(settingsMap)].some(
		(key) => BTHOME_DEVICE_REGEX.test(key) && isRemoteModel(statusMap[key] || settingsMap[key]),
	);
};

/**
 * Detects paired BTHome devices and Bluetooth remotes from current status/settings.
 * Callers own any hint handling and icon priority.
 */
export const getBTHomeStatusIconFlags = (
	device: Device,
	statusOverride?: ComponentMap,
	settingsOverride?: DeviceSettingsG2 | ComponentMap,
): BtHomeStatusIconFlags => {
	const maps = getComponentMaps(device, statusOverride, settingsOverride);

	if (!maps) {
		return EMPTY_FLAGS;
	}

	return {
		hasPairedBTHomeDevices: hasBTHomeDeviceComponent(maps.statusMap, maps.settingsMap),
		hasPairedBluetoothRemotes: hasPairedRemote(maps.statusMap, maps.settingsMap),
	};
};
